// DroppableDay.jsx
import React from "react";
import dayjs from "dayjs";
import { useDroppable } from "@dnd-kit/core";
import { Popover, Button } from "antd";

/**
 * Column for calendar (droppable).
 *
 * Пропсы:
 * - day:          dayjs day object
 * - isToday:      boolean — light "today"
 * - isHighlighted:boolean — out light
 * - t:            translate (t)
 * - isActionsOpen:boolean — popover is open
 * - onActionsOpenChange: (open:boolean)=>void — turn popover
 * - onCreatePost: (day: dayjs)=>void
 * - onCreateNote: (day: dayjs)=>void
 * - children:     ReactNode or function
 */
const DroppableDay = ({
                          day,
                          isToday = false,
                          isHighlighted = false,
                          t = (s) => s,
                          isActionsOpen = false,
                          onActionsOpenChange = () => {},
                          onCreatePost = () => {},
                          onCreateNote = () => {},
                          children,
                      }) => {
    const dayKey = day.format("YYYY-MM-DD");
    const isPast = day.isBefore(dayjs(), "day");
    // dnd-kit droppable
    const { setNodeRef, isOver } = useDroppable({
        id: dayKey,
        data: { type: "day", dayKey, dayObj: day },
    });

    const headerBg = isToday ? "bg-blue-50/60" : "bg-white/80";
    const colBg = isToday ? "bg-blue-50/40" : "bg-white";
    const ring = isOver || isHighlighted ? "ring-2 ring-amber-300 ring-offset-1" : "";

    return (
        <div
            ref={setNodeRef}
            className={`calendar-column border-r border-gray-200 min-w-[220px] ${colBg}`}
            data-day={dayKey}
        >
            {/* Title day */}
            <div
                className={`sticky top-0 z-10 flex flex-col items-center justify-center gap-0.5 p-3 border-b border-gray-200 ${headerBg} backdrop-blur`}
            >
                {/* Day week */}
                <div className="text-xs uppercase tracking-wide text-gray-500">
                    {day.format("ddd")}
                </div>

                {/* Число + actions */}
                <div className="flex items-center gap-2">
                    <div className={`text-2xl font-semibold ${isToday ? "text-blue-700" : "text-gray-800"}`}>
                        {day.format("D")}
                    </div>

                    {/* Button with popover */}
                    <Popover
                        trigger="click"
                        placement="bottom"
                        open={isActionsOpen}
                        onOpenChange={(open) => onActionsOpenChange(open)}
                        content={
                            <div className="flex flex-col gap-2 min-w-[220px]">
                                <Button
                                    type="primary"
                                    className="!bg-violet-600 hover:!bg-violet-800"
                                    disabled={isPast}
                                    onClick={() => {
                                        onCreatePost(day);
                                        onActionsOpenChange(false);
                                    }}
                                >
                                    {t("publicationCalendar.actions.createPost")}
                                </Button>
                                <Button
                                    onClick={() => {
                                        onCreateNote(day);
                                        onActionsOpenChange(false);
                                    }}
                                >
                                    {t("publicationCalendar.actions.createNote")}
                                </Button>
                            </div>
                        }
                    >
                        <Button size="small" className="!rounded-full" aria-label={t("publicationCalendar.actions.openMenu") || "Actions"}>
                            +
                        </Button>
                    </Popover>
                </div>

                {/* mouth and year */}
                <div className="text-[11px] text-gray-400">{day.format("MMM YYYY")}</div>
            </div>

            {/* Day content (dnd-zone) */}
            <div className={`day-posts p-3 space-y-3 min-h-[560px] ${ring}`}>
                {typeof children === "function" ? children(isOver) : children}
            </div>
        </div>
    );
};

export default DroppableDay;
